import {
  Typography,
  Stack,
  TextField,
  Button,
  Alert,
  styled,
  Box
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { ref, getStorage, uploadBytes, getDownloadURL } from 'firebase/storage';
import CustomSelect from '../../components/CustomSelect';
import { UserContext } from '../../components/UserContex';
import { getAccount, setAccount } from '../../services/Account';
import { getOrganizations, setClubAndTeam, setTeams } from '../../services/Organizations';

const Input = styled('input')({
  display: 'none',
});

const Image = styled('img')({
  width: '100%',
  height: 'auto',
  maxWidth: '200px',
  maxHeight: '200px',
  objectFit: 'cover',
  objectPosition: 'center',
  margin: 'auto',
});

const MyUser = () => {
  const [accountObj, setAccountObj] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [createAlert, setCreateAlert] = useState({ open: false });
  const [accountAlert, setAccountAlert] = useState({ open: false });
  const [profileImage, setProfileImage] = useState(null);
  const [showAliasInput, setShowAliasInput] = useState(false);

  const user = useContext(UserContext);

  useEffect(() => {
    if (user !== null) {
      if (JSON.parse(localStorage.getItem('accountObj'))?.expires > +new Date()) {
        // using "cached" version of user details
        setAccountObj({ ...JSON.parse(localStorage.getItem('accountObj')), uid: user.uid });
        if (JSON.parse(localStorage.getItem('accountObj')).alias) setShowAliasInput(true);
      } else {
        getAccount(user?.uid).then((response) => {
          if (response === null) {
            // no user details exists in the database
            setCreateAlert({
              open: true,
              type: 'info',
              message: 'Fullfør registreringen ved å fylle inn feltene nedenfor.',
            });
            setAccountObj({ uid: user.uid });
          } else {
            if (response.alias) setShowAliasInput(true);
            localStorage.setItem(
              'accountObj',
              JSON.stringify({ ...response, expires: new Date().setDate(new Date().getDate() + 7) })
            );
          }
        });
      }
    }
  }, [user]);

  // fetches all organizations from the database
  useEffect(() => getOrganizations().then(setOrganizations), []);

  useEffect(() => {
    // clean up image browser storage upon unmount
    return () => URL.revokeObjectURL(profileImage);
  }, [profileImage]);

  useEffect(() => {
    localStorage.removeItem('summaryObj');
  }, [showAliasInput]);

  const addOrganization = (accountObj) => {
    const { club, team } = accountObj;
    const organization = organizations.filter((organization) => organization.name === club);
    if (organization.length === 1) {
      if (organization[0]?.teams.filter((teams) => teams.name === team).length === 1) {
        // do nothing — everything exists from before
        return;
      } else {
        setTeams(club, team);
      }
    } else {
      setClubAndTeam(club, team);
    }
  };

  const postUpdates = async (accountObj) => {
    // removing expires attribute before posting to db
    const { expires, ...parsedObj } = accountObj;
    if (parsedObj.alias) {
      if (parsedObj.alias.length !== 11) {
        setAccountAlert({ open: true, type: 'error', message: 'Telefonnummer har ikke riktig lengde.' });
        return;
      }
    }

    await setAccount(parsedObj)
      .then((success) => {
        setAccountAlert({ open: true, type: 'success', message: 'Brukeren har blitt oppdatert!' });
        if (createAlert.open) {
          setCreateAlert({ open: false });
        }
        addOrganization(accountObj);
        localStorage.setItem(
            'accountObj',
            JSON.stringify({ ...accountObj, expires: new Date().setDate(new Date().getDate() + 7) })
        );
      })
      .catch((error) => {
        console.log(error);
        setAccountAlert({ open: true, type: 'error', message: 'Kunne ikke oppdatere bruker :(' });
      });
  };

  const updateAccount = async (updateObj) => {
    let accountObj = {...updateObj, identifier: user.phoneNumber ?? user.email};
    
    if (profileImage !== null) {
      const storageRef = ref(getStorage(), `profile-images/${accountObj.uid}`);
      await uploadBytes(storageRef, profileImage).then((snapshot) => {
        getDownloadURL(storageRef).then((imageUrl) => {
          accountObj = { ...accountObj, profileImage: imageUrl };
          postUpdates(accountObj);
        });
      });
    }
    postUpdates(accountObj);
  };

  return (
    <>
      <Typography variant="h4" sx={{ fontWeight: 'bold' }} gutterBottom>
        Min bruker
      </Typography>
      <Typography variant="body1" sx={{ maxWidth: '500px' }} gutterBottom>
        Her finner du dine brukeropplysninger.
      </Typography>
      {createAlert.open && (
        <Alert sx={{ margin: '1em auto' }} severity={createAlert.type}>
          {createAlert.message}
        </Alert>
      )}
      <Stack spacing={2} mt={3}>
        {(accountObj?.profileImage || profileImage) && (
          <Image src={(profileImage && URL.createObjectURL(profileImage)) ?? accountObj?.profileImage} alt="profilbilde" />
        )}
        <label htmlFor="contained-button-file">
          <Input
            accept="image/*"
            id="contained-button-file"
            multiple
            type="file"
            onChange={({ target }) => setProfileImage(target.files[0])}
          />
          <Box display="flex" justifyContent="center">
            <Button variant="contained" component="span">
              Last opp bilde
            </Button>
          </Box>
        </label>
        {profileImage && <Alert severity="info">Husk å trykke 'oppdater' nederst på siden for å lagre bildet.</Alert>}
        <TextField
          label="Navn"
          type="string"
          variant="outlined"
          value={accountObj?.name ?? ''}
          onChange={({ target }) => setAccountObj({ ...accountObj, name: target.value })}
        />
        <CustomSelect objKey="club" state={accountObj} setState={setAccountObj} values={organizations} label="Klubb" />
        <CustomSelect
          objKey="team"
          state={accountObj}
          setState={setAccountObj}
          values={organizations.filter((club) => club?.name === accountObj?.club)[0]?.teams ?? []}
          label="Lag"
        />
        <TextField
          label="Vervet av (fornavn og etternavn)"
          type="string"
          variant="outlined"
          value={accountObj?.recruitedBy ?? ''}
          onChange={({ target }) => setAccountObj({ ...accountObj, recruitedBy: target.value })}
        />
        <Button onClick={() => updateAccount(accountObj)} variant="contained" color="primary" size="large">
          Oppdater
        </Button>
        {accountAlert.open && <Alert severity={accountAlert.type}>{accountAlert.message}</Alert>}
      </Stack>
    </>
  );
};

export default MyUser;
